import { useState, useEffect } from "react";
import {
  collection,
  query,
  orderBy,
  getDocs,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  where,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "@/api/firebase/firebase";
import {
  AssessmentTemplate,
  CreateAssessmentTemplateRequest,
  UpdateAssessmentTemplateRequest,
} from "@/types/assessment";
import { useAuth } from "@/contexts/AuthContext";

// The effective default template for a type: the one explicitly marked as
// default, or — when none is marked — the first active one as listed on the
// Assessment Templates page (newest first). This way a template-driven form is
// always used when any template exists, instead of falling back to a
// hardcoded legacy form.
export const pickDefaultTemplate = <
  T extends {
    isDefault?: boolean;
    isActive?: boolean;
    createdAt?: Date | null;
  }
>(
  list: T[]
): T | undefined => {
  const active = list.filter((t) => t.isActive !== false);
  if (active.length === 0) return undefined;
  const explicit = active.find((t) => t.isDefault === true);
  if (explicit) return explicit;
  return [...active].sort(
    (a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0)
  )[0];
};

export const useAssessmentTemplates = (
  type?: "pre" | "post" | "trainer_feedback"
) => {
  const [templates, setTemplates] = useState<AssessmentTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useAuth();

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!currentUser?.id) {
        console.warn("No authenticated user found");
        setError("User not authenticated");
        return;
      }

      let templatesQuery = query(
        collection(db, "assessmentTemplates"),
        orderBy("createdAt", "desc")
      );

      if (type) {
        templatesQuery = query(
          collection(db, "assessmentTemplates"),
          where("type", "==", type)
        );
      }

      const snapshot = await getDocs(templatesQuery);

      const fetchedTemplates = snapshot.docs.map((doc) => {
        const data = doc.data();

        return {
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date(),
          lastUpdatedOn: doc.data().lastUpdatedOn?.toDate() || new Date(),
          name: doc.data().name || "",
          description: doc.data().description || "",
          questions: doc.data().questions || [],
          type: doc.data().type || "pre",
          createdBy: doc.data().createdBy || "",
          updatedBy: doc.data().updatedBy || "",
          isActive: doc.data().isActive ?? true,
          requiredFields: doc.data().requiredFields || [],
          totalPoints: doc.data().totalPoints || 0,
          isDefault: doc.data().isDefault === true,
        };
      }) as AssessmentTemplate[];

      setTemplates(fetchedTemplates);
    } catch (err) {
      console.error("Error fetching assessment templates:", err);
      if (err instanceof Error) {
        console.error("Error details:", {
          message: err.message,
          stack: err.stack,
        });
      }
      setError(
        err instanceof Error
          ? err.message
          : "Failed to fetch assessment templates"
      );
    } finally {
      setLoading(false);
    }
  };

  const createTemplate = async (
    templateData: CreateAssessmentTemplateRequest
  ) => {
    try {
      if (!currentUser?.id) {
        throw new Error("User not authenticated");
      }

      const docRef = await addDoc(collection(db, "assessmentTemplates"), {
        ...templateData,
        createdBy: currentUser.id,
        updatedBy: currentUser.id,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastUpdatedOn: serverTimestamp(),
        isActive: true,
      });

      const newTemplate: AssessmentTemplate = {
        id: docRef.id,
        ...templateData,
        createdBy: currentUser.id,
        updatedBy: currentUser.id,
        createdAt: new Date(),
        updatedAt: new Date(),
        lastUpdatedOn: new Date(),
        isActive: true,
      };

      setTemplates((prev) => [newTemplate, ...prev]);
      return newTemplate;
    } catch (err) {
      console.error("Error creating assessment template:", err);
      throw new Error("Failed to create assessment template");
    }
  };

  const updateTemplate = async (
    templateData: UpdateAssessmentTemplateRequest
  ) => {
    try {
      if (!currentUser?.id) {
        throw new Error("User not authenticated");
      }

      const { id, ...updateData } = templateData;
      const templateRef = doc(db, "assessmentTemplates", id);

      await updateDoc(templateRef, {
        ...updateData,
        updatedBy: currentUser.id,
        updatedAt: serverTimestamp(),
        lastUpdatedOn: serverTimestamp(),
      });

      setTemplates((prev) =>
        prev.map((template) =>
          template.id === id
            ? {
                ...template,
                ...updateData,
                updatedBy: currentUser.id,
                updatedAt: new Date(),
                lastUpdatedOn: new Date(),
              }
            : template
        )
      );
    } catch (err) {
      console.error("Error updating assessment template:", err);
      throw new Error("Failed to update assessment template");
    }
  };

  // Marks one template as the default for its type, clearing the flag from any
  // other template of the same type so there is never more than one.
  const setDefaultTemplate = async (
    id: string,
    templateType: "pre" | "post" | "trainer_feedback",
    isDefault: boolean
  ) => {
    try {
      if (!currentUser?.id) {
        throw new Error("User not authenticated");
      }

      const snapshot = await getDocs(
        query(
          collection(db, "assessmentTemplates"),
          where("type", "==", templateType)
        )
      );

      const batch = writeBatch(db);
      snapshot.docs.forEach((docSnap) => {
        const shouldBeDefault = isDefault && docSnap.id === id;
        // Only write the documents whose flag actually changes.
        if ((docSnap.data().isDefault === true) !== shouldBeDefault) {
          batch.update(doc(db, "assessmentTemplates", docSnap.id), {
            isDefault: shouldBeDefault,
            updatedBy: currentUser.id,
            updatedAt: serverTimestamp(),
          });
        }
      });
      await batch.commit();

      setTemplates((prev) =>
        prev.map((template) =>
          template.type === templateType
            ? { ...template, isDefault: isDefault && template.id === id }
            : template
        )
      );
    } catch (err) {
      console.error("Error setting default assessment template:", err);
      throw new Error("Failed to update the default template");
    }
  };

  const deleteTemplate = async (id: string) => {
    try {
      await deleteDoc(doc(db, "assessmentTemplates", id));
      setTemplates((prev) => prev.filter((template) => template.id !== id));
    } catch (err) {
      console.error("Error deleting assessment template:", err);
      throw new Error("Failed to delete assessment template");
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [type]);

  return {
    templates,
    loading,
    error,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    setDefaultTemplate,
    refreshTemplates: fetchTemplates,
  };
};
